/**
 * MobyCalendar™ — CalendarDayDialog
 *
 * Opened by clicking a day cell in CalendarPanel (via 'calendar-day-open' event).
 * Primary interaction surface for:
 *   1. Viewing the day's agenda
 *   2. Creating new events with inline form
 *   3. Inviting attendees (MobyNet checkmark / External pattern)
 *   4. Availability dots (green/red/grey) per attendee
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  XIcon,
  PlusIcon,
  ClockIcon,
  MapPinIcon,
  UserPlusIcon,
  CheckIcon,
  Loader2,
  Trash2Icon,
  PencilIcon,
  CrownIcon,
  UsersIcon,
} from 'lucide-react';
import { useDayEvents, useCalendarCRUD } from '@/hooks/useCalendarEvents';
import { useMobyDB, useSession, useMobyNodeClient, createAndSignGroupInvite, createGroupInviteMessage } from '@/components/provider';
import { useLiveQuery } from 'dexie-react-hooks';
import type { CalendarEvent, CalendarAttendee } from '@/lib/calendar-types';
import { CalendarRSVP, EventVisibility } from '@/lib/calendar-types';
import { MessageDestinationType, MessageBodyType, MessagePacketType, WiredMessageBody, WiredMessagePacket, type MobyDB } from '@/moby/db';
import { createAndSignContactDetails } from '@/moby/repo';
import { bytesToHex, hexToBytes } from '@noble/curves/abstract/utils';
import { ed25519 } from '@noble/curves/ed25519';
import { binencode } from '@/lib/binary';
import { toast } from 'sonner';
import { mui } from '@/mui';

// ─── Types ──────────────────────────────────────────────────────────────────

interface RecipientEntry {
  address: string;
  displayName: string;
  type: 'mobynet' | 'external';
  availability: 'free' | 'busy' | 'unknown';
}

interface RecipientLookup {
  status: 'idle' | 'checking' | 'found' | 'not_found';
  user?: { publicKey: string; preferredName?: string };
}

// ─── Email Helpers (client-side calendar invite notifications) ───────────────

interface EmailAccount {
  id: string;
  email: string;
  enabled: boolean;
  smtpHost: string;
  smtpPort: number;
  username: string;
  password: string;
  authType: "password" | "oauth2";
  accessToken?: string;
}

function getEmailGatewayUrl(): string {
  try {
    const stored = localStorage.getItem('moby_pref_emailGatewayUrl');
    if (stored) return stored;
    const legacy = localStorage.getItem('localPreferences');
    if (legacy) return JSON.parse(legacy).emailGatewayUrl || '';
  } catch { /* ignore */ }
  return '';
}

function getEmailAccounts(): EmailAccount[] {
  try {
    const stored = localStorage.getItem("moby_pref_emailAccounts");
    if (stored) return JSON.parse(stored) as EmailAccount[];
  } catch { /* ignore */ }
  return [];
}

function formatDateTimeForEmail(timestamp: number, allDay: boolean): string {
  if (!timestamp) return "TBD";
  const date = new Date(timestamp);
  if (allDay) {
    return date.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  }
  return date.toLocaleString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// ─── Email Template Helpers ──────────────────────────────────────────────────

function buildCalendarNotifyEmail(senderName: string, eventTitle: string, timeRange: string): string {
  return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto;">
  <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 24px; border-radius: 8px 8px 0 0;">
    <h2 style="color: white; margin: 0;">Calendar Invite</h2>
  </div>
  <div style="border: 1px solid #e0e0e0; border-top: none; padding: 24px; border-radius: 0 0 8px 8px;">
    <p><strong>${senderName}</strong> invited you to:</p>
    <h3 style="color: #333; margin: 8px 0;">${eventTitle}</h3>
    <p style="color: #666;">${timeRange}</p>
    <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;">
    <p style="color: #888; font-size: 13px;">Open MobyChat to view the full invite and RSVP.</p>
    <p style="color: #aaa; font-size: 11px;">MobyChat — Secure, decentralized messaging</p>
  </div>
</body>
</html>`;
}

function buildCalendarInviteEmail(
  senderName: string,
  eventTitle: string,
  timeRange: string,
  inviteLink: string,
  shareCode: string,
  groupName?: string,
): string {
  const groupNote = groupName
    ? `<p>This event is part of the group <strong>${groupName}</strong>. You'll be added to the group automatically when you connect.</p>`
    : '';
  return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto;">
  <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 24px; border-radius: 8px 8px 0 0;">
    <h2 style="color: white; margin: 0;">Calendar Invite</h2>
  </div>
  <div style="border: 1px solid #e0e0e0; border-top: none; padding: 24px; border-radius: 0 0 8px 8px;">
    <p><strong>${senderName}</strong> invited you to:</p>
    <h3 style="color: #333; margin: 8px 0;">${eventTitle}</h3>
    <p style="color: #666;">${timeRange}</p>
    ${groupNote}
    <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;">
    <p><strong>New to MobyChat?</strong><br>
    Use this Invite Code during signup: <strong style="color: #0066cc;">9MOBYCHAT2025INVITE999</strong></p>
    <p><a href="${inviteLink}" style="display: inline-block; padding: 12px 24px; background-color: #0066cc; color: white; text-decoration: none; border-radius: 6px; font-weight: bold;">Open Invite &amp; RSVP</a></p>
    <p style="margin-top: 24px; color: #666; font-size: 13px;">Or manually paste this code in MobyChat:</p>
    <p style="font-family: monospace; background-color: #f5f5f5; padding: 12px; border-radius: 4px; word-break: break-all; font-size: 12px;">${shareCode}</p>
    <hr style="border: none; border-top: 1px solid #ddd; margin: 24px 0;">
    <p style="color: #888; font-size: 12px;">MobyChat — Secure, decentralized messaging</p>
  </div>
</body>
</html>`;
}

/**
 * Fire-and-forget: send calendar invite emails.
 * - MobyNet contacts: notification email only (P2P CALENDAR_INVITE handles actual delivery).
 * - External recipients: full invite link email (same pattern as contacts-drawer) +
 *   db.pendingInvites entry with calendarEventId so CONTACT_ADDED_NOTIFICATION handler
 *   auto-delivers the CALENDAR_INVITE P2P message after the recipient joins and connects.
 */
async function sendCalendarInviteEmails(
  recipients: RecipientEntry[],
  event: { id: string; title: string; startTime: number; endTime: number; allDay?: boolean },
  session: { address: string; secret: string; name?: string },
  db: MobyDB,
  groupAddress?: string,
  groupName?: string,
) {
  const gatewayUrl = getEmailGatewayUrl();
  const accounts = getEmailAccounts().filter(a => a.enabled);
  if (!gatewayUrl || accounts.length === 0 || recipients.length === 0) return;

  const account = accounts[0];
  const credentials: { user: string; pass?: string; accessToken?: string } = { user: account.username };
  if (account.authType === 'oauth2' && account.accessToken) {
    credentials.accessToken = account.accessToken;
  } else {
    credentials.pass = account.password;
  }

  const senderName = session.name || session.address.slice(0, 8);
  const startStr = formatDateTimeForEmail(event.startTime, !!event.allDay);
  const endStr = event.endTime ? formatDateTimeForEmail(event.endTime, !!event.allDay) : '';
  const timeRange = event.allDay ? startStr : (endStr ? `${startStr} — ${endStr}` : startStr);

  // Generate invite link for external recipients using our contact key (same as contacts-drawer)
  const contactDetails = createAndSignContactDetails({ ...session, name: session.name ?? '' });
  const shareCode = contactDetails ? bytesToHex(contactDetails) : '';
  const inviteCode = '9MOBYCHAT2025INVITE999';
  const inviteLink = shareCode ? `${window.location.origin}/chats/?invite=${inviteCode}#${shareCode}` : '';

  for (const recipient of recipients) {
    try {
      if (!inviteLink) continue;

      let toEmail: string;
      if (recipient.type === 'mobynet') {
        // MobyNet contact: look up their registered email so we can send the CTA invite.
        // We still send the full CTA invite (not just a notify) because P2P delivery may
        // not have reached them yet (peer offline, queued in pendingDeliveries).
        const lookupRes = await fetch(`${gatewayUrl}/api/user/lookup?publicKey=${encodeURIComponent(recipient.address)}`);
        if (!lookupRes.ok) continue;
        const lookupData = await lookupRes.json();
        const recipientEmail = lookupData.user?.email;
        if (!recipientEmail) continue;
        toEmail = recipientEmail;
      } else {
        // External email recipient
        toEmail = recipient.address;
      }

      const html = buildCalendarInviteEmail(senderName, event.title, timeRange, inviteLink, shareCode, groupName);
      const response = await fetch(`${gatewayUrl}/api/email/smtp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host: account.smtpHost, port: account.smtpPort,
          secure: account.smtpPort === 465, credentials,
          from: account.email, to: toEmail,
          subject: `${senderName} invited you to: ${event.title}`,
          body: html, html: true,
        }),
      });

      if (response.ok) {
        // Write pendingInvites for ALL recipients (MobyNet + external).
        // When a MobyNet user re-onboards with a new keypair, their address changes
        // but their email stays the same. The CONTACT_ADDED_NOTIFICATION handler uses
        // pendingInvites to auto-deliver the calendar invite on connect.
        // For already-connected peers, the deliveredP2P check prevents re-delivery.
        await db.pendingInvites.add({
          id: crypto.randomUUID(),
          recipientEmail: toEmail,
          senderAddress: session.address,
          senderKeyString: shareCode,
          status: 'pending',
          createdAt: new Date(),
          expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          groupAddress: groupAddress || undefined,
          groupName: groupName || undefined,
          calendarEventId: event.id,
          calendarEventTitle: event.title,
          syncedToServer: 0,
        } as any);
        console.log('[CALENDAR_EMAIL] Sent CTA invite + wrote pendingInvite', {
          to: toEmail, eventId: event.id, hasGroup: !!groupAddress, recipientType: recipient.type,
        });
      }
    } catch (err) {
      console.warn('[CALENDAR_EMAIL] Failed to send to', recipient.address, err);
    }
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function dayBounds(dateMs: number): [number, number] {
  const d = new Date(dateMs);
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const end = start + 86_400_000 - 1;
  return [start, end];
}

function formatDayHeader(dateMs: number): string {
  return new Date(dateMs).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Check a single quarter-hour slot in an 84-byte availability bitmap */
function checkSlotAvailability(
  bitmap: string | undefined,
  windowStart: number | undefined,
  slotTimeMs: number,
): 'free' | 'busy' | 'unknown' {
  if (!bitmap || !windowStart) return 'unknown';
  try {
    const bytes = Uint8Array.from(atob(bitmap), c => c.charCodeAt(0));
    const diffMs = slotTimeMs - windowStart;
    if (diffMs < 0 || diffMs >= 7 * 86_400_000) return 'unknown';
    const slotIndex = Math.floor(diffMs / (15 * 60_000));
    const byteIdx = Math.floor(slotIndex / 8);
    const bitIdx = 7 - (slotIndex % 8);
    if (byteIdx >= bytes.length) return 'unknown';
    return (bytes[byteIdx] >> bitIdx) & 1 ? 'busy' : 'free';
  } catch {
    return 'unknown';
  }
}

// ─── Group Chat Creation for Calendar Events ────────────────────────────────

/**
 * Creates a group chat programmatically for a calendar event.
 * Generates keypair, creates GROUP_JOIN message, stores in DB,
 * sends P2P group invites to connected MobyNet contacts.
 * Returns the new group address hex string.
 */
async function createGroupChatForEvent(
  db: MobyDB,
  session: { address: string; secret: string; name?: string },
  node: ReturnType<typeof useMobyNodeClient>,
  groupName: string,
  contactAddresses: string[],
): Promise<string> {
  // Safety net: never invite self (prevents storeMessage from===to rejection)
  contactAddresses = contactAddresses.filter(addr => addr !== session.address);
  const secret = ed25519.utils.randomPrivateKey();
  const address = ed25519.getPublicKey(secret);
  const groupAddressHex = bytesToHex(address);

  const first = {
    type: MessageBodyType.GROUP_JOIN as const,
    groupAddress: address,
    groupInvite: createAndSignGroupInvite(groupAddressHex, session),
    from: {
      address: hexToBytes(session.address),
      nonce: 1,
    },
    version: 1,
    timestamp: BigInt(Date.now()),
  };

  const firstMessage = {
    signature: ed25519.sign(
      binencode(WiredMessageBody, first),
      hexToBytes(session.secret),
    ),
    body: first,
  };

  const inviteMessages = await Promise.all(
    contactAddresses.map(addr =>
      createGroupInviteMessage(db, groupAddressHex, session, addr)
    ),
  );

  // FIX 1 (CALENDAR_PHANTOM): Only store DM invite messages for contacts that
  // already have an existing DM chat. storeMessage() auto-creates a DM chat if
  // one doesn't exist (db.ts:2676), which produces phantom "User-<key>" entries
  // for offline contacts who never sent PROFILE_INFO. Offline contacts receive
  // the group invite via the email + pendingInvite + AUTO_GROUP_INVITE path.
  const inviteMessagesWithChat: typeof inviteMessages = [];
  for (const msg of inviteMessages) {
    const addr = bytesToHex(msg.body.to.address);
    const existingChat = await db.chatByPeerAddress(addr);
    if (existingChat) {
      inviteMessagesWithChat.push(msg);
    } else {
      console.log('[CALENDAR_GROUP] Skipping DM invite store for chatless contact — will use email path', {
        contact: addr.slice(0, 8),
        group: groupAddressHex.slice(0, 16),
      });
    }
  }

  await db.transaction('rw', db.chats, db.messages, async () => {
    await db.chats.add({
      address: groupAddressHex,
      secret: bytesToHex(secret),
      type: MessageDestinationType.GROUP,
      name: groupName,
      peers: [],
      nonces: {},
    } as any);
    await db.storeGroupJoinMessage(session.address, firstMessage as any);
    await Promise.all(
      inviteMessagesWithChat.map(msg => db.storeMessage(session.address, msg)),
    );
  });

  // FIX 1 (CALENDAR_PHANTOM): Only send P2P invites to already-connected peers.
  // Use peers.get() instead of getOrCreatePeer() to avoid creating phantom
  // RTCPeerConnection objects for offline contacts (which enter CONNECTION_RETRY
  // loop, generate 202 signaling noise, and consume resources until STALE_NEW_TIMEOUT).
  for (const inviteMessage of inviteMessagesWithChat) {
    const addr = bytesToHex(inviteMessage.body.to.address);
    const peer = node.peers?.get(addr);
    if (!peer || peer.connection.connectionState !== 'connected') continue;
    void peer.chat.send(
      binencode(WiredMessagePacket, {
        type: MessagePacketType.MESSAGE,
        message: inviteMessage,
      }),
    );
  }

  console.log('[CALENDAR_GROUP] Auto-created group for event', {
    groupName, groupAddress: groupAddressHex.slice(0, 16), members: contactAddresses.length,
  });
  return groupAddressHex;
}

// ─── Agenda Item ────────────────────────────────────────────────────────────

function AgendaItem({
  event,
  onDelete,
  onEdit,
}: {
  event: CalendarEvent;
  onDelete: (id: string) => void;
  onEdit?: (id: string) => void;
}) {
  return (
    <div className="bg-surface-container-highest rounded-lg p-3 flex items-start gap-3 group">
      <div className="flex-1 min-w-0">
        <div className="text-body-md font-medium truncate">{event.title}</div>
        <div className="text-body-sm text-on-surface-variant flex items-center gap-1">
          <ClockIcon size={12} />
          {event.allDay
            ? 'All day'
            : `${formatTime(event.startTime)} \u2013 ${formatTime(event.endTime)}`}
        </div>
        {event.location && (
          <div className="text-body-sm text-on-surface-variant flex items-center gap-1 mt-0.5">
            <MapPinIcon size={12} /> {event.location}
          </div>
        )}
        {event.attendees.length > 0 && (
          <div className="text-body-sm text-on-surface-variant mt-1">
            {event.attendees.length} attendee{event.attendees.length !== 1 ? 's' : ''}
            {' \u2014 '}
            {event.attendees.filter(a => a.rsvp === CalendarRSVP.ACCEPTED).length} accepted
          </div>
        )}
      </div>
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
        {onEdit && (
          <button
            onClick={() => onEdit(event.id)}
            className="p-1 rounded-full hover:bg-on-surface/10 text-on-surface-variant transition"
            title="Edit event"
          >
            <PencilIcon size={14} />
          </button>
        )}
        <button
          onClick={() => onDelete(event.id)}
          className="p-1 rounded-full hover:bg-error/10 text-error transition"
          title="Delete event"
        >
          <Trash2Icon size={16} />
        </button>
      </div>
    </div>
  );
}

// ─── Availability Dot ───────────────────────────────────────────────────────

function AvailabilityDot({ status }: { status: 'free' | 'busy' | 'unknown' }) {
  const colors = {
    free: 'bg-green-500',
    busy: 'bg-red-500',
    unknown: 'bg-on-surface-variant/40',
  };
  const labels = { free: 'Free', busy: 'Busy', unknown: 'Unknown' };
  return (
    <span
      className={`inline-block size-2.5 rounded-full ${colors[status]}`}
      title={labels[status]}
    />
  );
}

// ─── Recipient Chip ─────────────────────────────────────────────────────────

function RecipientChip({
  entry,
  onRemove,
}: {
  entry: RecipientEntry;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-surface-container-highest rounded-full pl-2.5 pr-1 py-1 text-body-sm">
      <AvailabilityDot status={entry.availability} />
      <span className="truncate max-w-[140px]">{entry.displayName}</span>
      {entry.type === 'mobynet' && (
        <span className="text-tertiary" title="MobyNet contact">
          <CheckIcon size={12} />
        </span>
      )}
      <button
        onClick={onRemove}
        className="p-0.5 rounded-full hover:bg-on-surface/10"
        title="Remove"
      >
        <XIcon size={12} />
      </button>
    </span>
  );
}

// ─── New Event Form ─────────────────────────────────────────────────────────

function NewEventForm({
  dateMs,
  onCreated,
  onCancel,
}: {
  dateMs: number;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const session = useSession();
  const db = useMobyDB();
  const node = useMobyNodeClient();
  const { createEvent } = useCalendarCRUD();

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startHour, setStartHour] = useState(9);
  const [startMinute, setStartMinute] = useState(0);
  const [endHour, setEndHour] = useState(10);
  const [endMinute, setEndMinute] = useState(0);
  const [allDay, setAllDay] = useState(false);
  const [visibility, setVisibility] = useState<EventVisibility>(EventVisibility.BUSY);
  const [submitting, setSubmitting] = useState(false);

  // Group meeting
  const [selectedGroupAddress, setSelectedGroupAddress] = useState<string | null>(null);
  const [autoCreateGroup, setAutoCreateGroup] = useState(false);

  // Query groups where current user is organizer (has secret)
  const organizerGroups = useLiveQuery(
    async () => {
      const chats = await db.chats.where('type').equals(MessageDestinationType.GROUP).toArray();
      return chats.filter(c => !!c.secret).map(c => ({
        address: c.address,
        name: c.name ?? 'Group',
        peers: c.peers,
      }));
    },
    [],
  );

  // When a group is selected, auto-populate attendees from group members
  const handleGroupSelect = useCallback(async (groupAddress: string | null) => {
    setSelectedGroupAddress(groupAddress);
    if (!groupAddress || !session) {
      return;
    }
    const group = (organizerGroups ?? []).find(g => g.address === groupAddress);
    if (!group) return;

    // Build recipient entries from group peers (excluding self)
    const groupRecipients: RecipientEntry[] = [];
    for (const peerAddr of group.peers) {
      if (peerAddr === session.address) continue;
      const contact = await db.contacts.get(peerAddr);
      const name = contact?.card?.name || contact?.nickname || peerAddr.slice(0, 8);
      groupRecipients.push({
        address: peerAddr,
        displayName: name,
        type: 'mobynet',
        availability: 'unknown', // Will be checked lazily
      });
    }
    setRecipients(groupRecipients);
  }, [organizerGroups, session, db]);

  // Recipients
  const [recipients, setRecipients] = useState<RecipientEntry[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [recipientLookup, setRecipientLookup] = useState<RecipientLookup>({ status: 'idle' });
  const lookupTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Contact list for autocomplete
  const contacts = useLiveQuery(
    () => db.contacts.toArray(),
    [],
  );

  // Filtered contacts for dropdown
  const filteredContacts = useMemo(() => {
    if (!contacts || !recipientInput.trim()) return contacts ?? [];
    const q = recipientInput.toLowerCase();
    return contacts.filter(c => {
      const name = (c.card?.name || c.nickname || c.address).toLowerCase();
      return name.includes(q) || c.address.toLowerCase().includes(q);
    });
  }, [contacts, recipientInput]);

  // Compute event start time for availability checks
  const eventStartMs = useMemo(() => {
    const d = new Date(dateMs);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), startHour, startMinute).getTime();
  }, [dateMs, startHour, startMinute]);

  // Email lookup for external recipients (debounced)
  useEffect(() => {
    if (!recipientInput || !recipientInput.includes('@')) {
      setRecipientLookup({ status: 'idle' });
      return;
    }
    if (lookupTimeoutRef.current) clearTimeout(lookupTimeoutRef.current);
    setRecipientLookup({ status: 'checking' });

    lookupTimeoutRef.current = setTimeout(async () => {
      const gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim();
      if (!gatewayUrl) {
        setRecipientLookup({ status: 'not_found' });
        return;
      }
      try {
        const res = await fetch(`${gatewayUrl}/api/user/lookup-email?email=${encodeURIComponent(recipientInput)}`);
        const data = await res.json();
        if (data.success && data.found && data.user) {
          setRecipientLookup({ status: 'found', user: data.user });
        } else {
          setRecipientLookup({ status: 'not_found' });
        }
      } catch {
        setRecipientLookup({ status: 'not_found' });
      }
    }, 600);

    return () => { if (lookupTimeoutRef.current) clearTimeout(lookupTimeoutRef.current); };
  }, [recipientInput]);

  // Fetch availability for a MobyNet contact — also called lazily by the useEffect below
  const fetchAvailability = useCallback(async (address: string): Promise<'free' | 'busy' | 'unknown'> => {
    try {
      const gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim();
      if (!gatewayUrl) return 'unknown';
      const res = await fetch(`${gatewayUrl}/api/email/presence?addresses=${encodeURIComponent(address)}`);
      if (!res.ok) return 'unknown';
      const data = await res.json();
      const peerPresence = data.presence?.[address];
      if (peerPresence?.availability) {
        return checkSlotAvailability(peerPresence.availability.slots, peerPresence.availability.windowStart, eventStartMs);
      }
    } catch { /* ignore */ }
    return 'unknown';
  }, [eventStartMs]);

  // Add a MobyNet contact as recipient
  const addContact = useCallback(async (address: string, displayName: string) => {
    if (recipients.some(r => r.address === address)) return;
    // Prevent adding own key as MobyNet peer (would crash storeMessage from===to).
    // Users CAN still invite their own email as an external recipient for multi-device onboarding.
    if (session && address === session.address) {
      toast.info('Use your email address to invite another device');
      setRecipientInput('');
      setShowContactPicker(false);
      return;
    }
    const availability = await fetchAvailability(address);
    setRecipients(prev => [...prev, { address, displayName, type: 'mobynet', availability }]);
    setRecipientInput('');
    setShowContactPicker(false);
  }, [recipients, fetchAvailability, session]);

  // Lazy availability check: fire for any MobyNet recipients still at 'unknown'
  // This covers group-selector-added contacts which skip the addContact() path
  useEffect(() => {
    const unknownMobyNet = recipients.filter(r => r.type === 'mobynet' && r.availability === 'unknown');
    if (unknownMobyNet.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const r of unknownMobyNet) {
        if (cancelled) break;
        const avail = await fetchAvailability(r.address);
        if (!cancelled) {
          setRecipients(prev =>
            prev.map(p => p.address === r.address ? { ...p, availability: avail } : p)
          );
        }
      }
    })();
    return () => { cancelled = true; };
  }, [recipients.map(r => r.address + r.availability).join(','), eventStartMs, fetchAvailability]);

  // Add external email recipient
  const addExternalRecipient = useCallback(() => {
    const email = recipientInput.trim();
    if (!email || recipients.some(r => r.address === email)) return;
    if (recipientLookup.status === 'found' && recipientLookup.user) {
      // Email resolved to a MobyNet user — but if it's our own key, add as external
      // so the email CTA is sent (multi-device / new-peer onboarding via own email)
      if (session && recipientLookup.user.publicKey === session.address) {
        setRecipients(prev => [...prev, { address: email, displayName: email, type: 'external', availability: 'unknown' }]);
      } else {
        addContact(recipientLookup.user.publicKey, recipientLookup.user.preferredName || email);
      }
    } else {
      // External email recipient
      setRecipients(prev => [...prev, { address: email, displayName: email, type: 'external', availability: 'unknown' }]);
    }
    setRecipientInput('');
    setRecipientLookup({ status: 'idle' });
  }, [recipientInput, recipientLookup, recipients, addContact]);

  const removeRecipient = useCallback((address: string) => {
    setRecipients(prev => prev.filter(r => r.address !== address));
  }, []);

  // Auto-toggle group creation when >1 recipients and no existing group selected
  useEffect(() => {
    if (recipients.length > 1 && !selectedGroupAddress) {
      setAutoCreateGroup(true);
    } else if (selectedGroupAddress || recipients.length <= 1) {
      setAutoCreateGroup(false);
    }
  }, [recipients.length, selectedGroupAddress]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim() || !session) return;
    setSubmitting(true);
    try {
      const d = new Date(dateMs);
      const start = allDay
        ? new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
        : new Date(d.getFullYear(), d.getMonth(), d.getDate(), startHour, startMinute).getTime();
      const end = allDay
        ? start + 86_400_000 - 1
        : new Date(d.getFullYear(), d.getMonth(), d.getDate(), endHour, endMinute).getTime();

      const attendees: CalendarAttendee[] = recipients.map(r => ({
        address: r.address,
        displayName: r.displayName,
        rsvp: CalendarRSVP.PENDING,
        isOrganizer: false,
      }));

      // Auto-create group chat if toggle is ON
      let groupAddress = selectedGroupAddress || undefined;
      if (autoCreateGroup && !groupAddress && session) {
        const mobynetAddresses = recipients.filter(r => r.type === 'mobynet').map(r => r.address);
        groupAddress = await createGroupChatForEvent(db, session, node, title.trim(), mobynetAddresses);
        toast.success(`Group "${title.trim()}" created`);
      }

      const eventData = {
        title: title.trim(),
        description: description.trim() || undefined,
        location: location.trim() || undefined,
        startTime: start,
        endTime: end,
        allDay,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        visibility,
        reminders: [{ minutesBefore: 15, method: 'notification' as any }],
        attendees,
        groupAddress,
      };
      const createdEvent = await createEvent(eventData);

      // Fire-and-forget: email invite notifications
      // External recipients get a full invite link email + pendingInvites entry for auto P2P delivery
      // MobyNet contacts get a notification email only (P2P CALENDAR_INVITE already sent)
      if (recipients.length > 0) {
        const evtGroupName = groupAddress
          ? ((organizerGroups ?? []).find(g => g.address === groupAddress)?.name || title.trim())
          : undefined;
        sendCalendarInviteEmails(
          recipients,
          { id: createdEvent.id, title: eventData.title, startTime: start, endTime: end, allDay },
          session,
          db,
          groupAddress,
          evtGroupName,
        ).catch(err => console.warn('[CALENDAR_EMAIL] Email notifications failed (non-fatal):', err));
      }

      onCreated();
    } catch (err) {
      console.error('[CALENDAR_DAY_DIALOG] Create event failed:', err);
    } finally {
      setSubmitting(false);
    }
  }, [title, description, location, dateMs, startHour, startMinute, endHour, endMinute, allDay, visibility, recipients, selectedGroupAddress, autoCreateGroup, session, createEvent, onCreated, db, node, organizerGroups]);

  return (
    <div className="space-y-4">
      <h3 className="text-title-sm text-primary flex items-center gap-2">
        <PlusIcon size={16} /> New Event
      </h3>

      {/* Title */}
      <input
        type="text"
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Event title"
        className="w-full rounded-lg bg-surface-container px-3 py-2.5 text-body-md text-on-surface border border-outline-variant focus:border-primary outline-none"
        autoFocus
      />

      {/* Group meeting selector */}
      {organizerGroups && organizerGroups.length > 0 && (
        <div className="flex items-center gap-2">
          <label className="text-label-sm text-on-surface-variant flex items-center gap-1">
            <UsersIcon size={14} /> Group:
          </label>
          <select
            value={selectedGroupAddress ?? ''}
            onChange={e => handleGroupSelect(e.target.value || null)}
            title="Select group for group meeting"
            className="flex-1 bg-surface-container rounded-lg px-2 py-1.5 border border-outline-variant text-body-sm"
          >
            <option value="">P2P meeting (no group)</option>
            {organizerGroups.map(g => (
              <option key={g.address} value={g.address}>
                {g.name} ({g.peers.length} members)
              </option>
            ))}
          </select>
          {selectedGroupAddress && (
            <span title="You are the organizer">
              <CrownIcon size={14} className="text-tertiary shrink-0" />
            </span>
          )}
        </div>
      )}

      {/* All-day toggle + time pickers */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer text-body-sm">
          <input
            type="checkbox"
            checked={allDay}
            onChange={e => setAllDay(e.target.checked)}
            className="accent-primary size-4"
          />
          All day
        </label>
        {!allDay && (
          <div className="flex items-center gap-1 text-body-sm">
            <select
              value={startHour}
              onChange={e => setStartHour(Number(e.target.value))}
              title="Start hour"
              className="bg-surface-container rounded px-1.5 py-1 border border-outline-variant text-body-sm"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>
              ))}
            </select>
            <span>:</span>
            <select
              value={startMinute}
              onChange={e => setStartMinute(Number(e.target.value))}
              title="Start minute"
              className="bg-surface-container rounded px-1.5 py-1 border border-outline-variant text-body-sm"
            >
              {[0, 15, 30, 45].map(m => (
                <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
              ))}
            </select>
            <span className="mx-1">–</span>
            <select
              value={endHour}
              onChange={e => setEndHour(Number(e.target.value))}
              title="End hour"
              className="bg-surface-container rounded px-1.5 py-1 border border-outline-variant text-body-sm"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>
              ))}
            </select>
            <span>:</span>
            <select
              value={endMinute}
              onChange={e => setEndMinute(Number(e.target.value))}
              title="End minute"
              className="bg-surface-container rounded px-1.5 py-1 border border-outline-variant text-body-sm"
            >
              {[0, 15, 30, 45].map(m => (
                <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Location */}
      <input
        type="text"
        value={location}
        onChange={e => setLocation(e.target.value)}
        placeholder="Location (optional)"
        className="w-full rounded-lg bg-surface-container px-3 py-2 text-body-sm text-on-surface border border-outline-variant focus:border-primary outline-none"
      />

      {/* Description */}
      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Description (optional)"
        rows={2}
        className="w-full rounded-lg bg-surface-container px-3 py-2 text-body-sm text-on-surface border border-outline-variant focus:border-primary outline-none resize-none"
      />

      {/* Visibility */}
      <div className="flex items-center gap-2">
        <label className="text-label-sm text-on-surface-variant">Show as:</label>
        <select
          value={visibility}
          onChange={e => setVisibility(e.target.value as EventVisibility)}
          title="Event visibility"
          className="bg-surface-container rounded px-2 py-1 border border-outline-variant text-body-sm"
        >
          <option value="busy">Busy</option>
          <option value="free">Free</option>
          <option value="tentative">Tentative</option>
        </select>
      </div>

      {/* ── Recipient Field ── */}
      <div className="space-y-2">
        <label className="text-label-md text-on-surface flex items-center gap-1">
          <UserPlusIcon size={14} /> Invite attendees
        </label>

        {/* Recipient chips */}
        {recipients.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {recipients.map(r => (
              <RecipientChip
                key={r.address}
                entry={r}
                onRemove={() => removeRecipient(r.address)}
              />
            ))}
          </div>
        )}

        {/* Input + lookup */}
        <div className="relative">
          <input
            type="text"
            value={recipientInput}
            onChange={e => {
              setRecipientInput(e.target.value);
              setShowContactPicker(true);
            }}
            onFocus={() => setShowContactPicker(true)}
            onKeyDown={e => {
              if (e.key === 'Enter' && recipientInput.includes('@')) {
                e.preventDefault();
                addExternalRecipient();
              }
            }}
            placeholder="Contact name or email..."
            className="w-full rounded-lg bg-surface-container px-3 py-2 text-body-sm text-on-surface border border-outline-variant focus:border-primary outline-none pr-28"
          />
          {recipientLookup.status !== 'idle' && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {recipientLookup.status === 'checking' && (
                <span className="text-label-sm text-on-surface-variant flex items-center gap-1">
                  <Loader2 size={12} className="animate-spin" /> Checking...
                </span>
              )}
              {recipientLookup.status === 'found' && (
                <span className="text-label-sm px-2 py-0.5 rounded bg-tertiary/20 text-tertiary">
                  MobyNet &#10003;
                </span>
              )}
              {recipientLookup.status === 'not_found' && (
                <span className="text-label-sm px-2 py-0.5 rounded bg-surface-container-highest text-on-surface-variant">
                  External
                </span>
              )}
            </div>
          )}

          {/* Contact dropdown */}
          {showContactPicker && filteredContacts && filteredContacts.length > 0 && !recipientInput.includes('@') && (
            <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-surface-container-high border border-outline-variant rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {filteredContacts.slice(0, 8).map(c => (
                <button
                  key={c.address}
                  onClick={() => addContact(c.address, c.card?.name || c.nickname || c.address.slice(0, 8))}
                  className="w-full text-left px-3 py-2 text-body-sm hover:bg-on-surface/5 flex items-center gap-2 transition"
                >
                  <CheckIcon size={12} className="text-tertiary" />
                  <span className="truncate">{c.card?.name || c.nickname || c.address.slice(0, 8)}</span>
                  <span className="ml-auto text-label-sm text-on-surface-variant">MobyNet</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Add external email button */}
        {recipientInput.includes('@') && recipientLookup.status !== 'checking' && (
          <button
            onClick={addExternalRecipient}
            className="text-label-sm text-primary hover:underline"
          >
            + Add &quot;{recipientInput}&quot; as {recipientLookup.status === 'found' ? 'MobyNet contact' : 'external recipient'}
          </button>
        )}
      </div>

      {/* Auto-create group toggle */}
      {recipients.length > 1 && !selectedGroupAddress && (
        <label className="flex items-center gap-2 cursor-pointer text-body-sm bg-tertiary/10 rounded-lg p-2.5">
          <input
            type="checkbox"
            checked={autoCreateGroup}
            onChange={e => setAutoCreateGroup(e.target.checked)}
            className="accent-tertiary size-4"
          />
          <UsersIcon size={14} className="text-tertiary" />
          <span>Create group chat &ldquo;{title.trim() || 'Untitled'}&rdquo;</span>
        </label>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <button
          onClick={handleSubmit}
          disabled={!title.trim() || submitting}
          className="flex-1 rounded-full bg-primary text-on-primary py-2.5 text-label-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting && <Loader2 size={16} className="animate-spin" />}
          Create Event
        </button>
        <button
          onClick={onCancel}
          className="rounded-full border border-outline-variant px-4 py-2.5 text-label-lg text-on-surface hover:bg-on-surface/5 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Edit Event Form ─────────────────────────────────────────────────────────

function EditEventForm({
  event,
  onClose,
}: {
  event: CalendarEvent;
  onClose: () => void;
}) {
  const db = useMobyDB();
  const session = useSession();
  const node = useMobyNodeClient();
  const { updateEvent } = useCalendarCRUD();

  // Pre-populate form state from existing event
  const [title, setTitle] = useState(event.title);
  const [description, setDescription] = useState(event.description || '');
  const [location, setLocation] = useState(event.location || '');
  const eventDate = useMemo(() => new Date(event.startTime), [event.startTime]);
  const [startHour, setStartHour] = useState(eventDate.getHours());
  const [startMinute, setStartMinute] = useState(eventDate.getMinutes());
  const endDate = useMemo(() => new Date(event.endTime), [event.endTime]);
  const [endHour, setEndHour] = useState(endDate.getHours());
  const [endMinute, setEndMinute] = useState(endDate.getMinutes());
  const [allDay, setAllDay] = useState(event.allDay || false);
  const [visibility, setVisibility] = useState<EventVisibility>(event.visibility || EventVisibility.BUSY);
  const [saving, setSaving] = useState(false);

  // Group
  const [autoCreateGroup, setAutoCreateGroup] = useState(false);
  const organizerGroups = useLiveQuery(
    async () => {
      const chats = await db.chats.where('type').equals(MessageDestinationType.GROUP).toArray();
      return chats.filter(c => !!c.secret).map(c => ({ address: c.address, name: c.name ?? 'Group', peers: c.peers }));
    },
    [],
  );

  // Recipients — initialise from event.attendees (excluding organizer)
  const [recipients, setRecipients] = useState<RecipientEntry[]>(() =>
    event.attendees
      .filter(a => !a.isOrganizer)
      .map(a => ({
        address: a.address,
        displayName: a.displayName || a.address.slice(0, 8),
        type: (a.address.includes('@') ? 'external' : 'mobynet') as 'external' | 'mobynet',
        availability: 'unknown' as const,
      })),
  );
  const originalAttendees = useMemo(() => new Set(event.attendees.map(a => a.address)), [event.id]);

  // Recipient input + contact picker
  const [recipientInput, setRecipientInput] = useState('');
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [recipientLookup, setRecipientLookup] = useState<RecipientLookup>({ status: 'idle' });
  const lookupTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const contacts = useLiveQuery(() => db.contacts.toArray(), []);

  const filteredContacts = useMemo(() => {
    if (!contacts || !recipientInput.trim()) return contacts ?? [];
    const q = recipientInput.toLowerCase();
    return contacts.filter(c => {
      const name = (c.card?.name || c.nickname || c.address).toLowerCase();
      return name.includes(q) || c.address.toLowerCase().includes(q);
    });
  }, [contacts, recipientInput]);

  const eventStartMs = useMemo(() => {
    return new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), startHour, startMinute).getTime();
  }, [eventDate, startHour, startMinute]);

  // Auto-toggle group creation when attendees grow to >1 and event has no group
  useEffect(() => {
    if (recipients.length > 1 && !event.groupAddress) {
      setAutoCreateGroup(true);
    } else if (event.groupAddress || recipients.length <= 1) {
      setAutoCreateGroup(false);
    }
  }, [recipients.length, event.groupAddress]);

  // Email lookup (debounced)
  useEffect(() => {
    if (!recipientInput || !recipientInput.includes('@')) {
      setRecipientLookup({ status: 'idle' });
      return;
    }
    if (lookupTimeoutRef.current) clearTimeout(lookupTimeoutRef.current);
    setRecipientLookup({ status: 'checking' });
    lookupTimeoutRef.current = setTimeout(async () => {
      const gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim();
      if (!gatewayUrl) { setRecipientLookup({ status: 'not_found' }); return; }
      try {
        const res = await fetch(`${gatewayUrl}/api/user/lookup-email?email=${encodeURIComponent(recipientInput)}`);
        const data = await res.json();
        if (data.success && data.found && data.user) {
          setRecipientLookup({ status: 'found', user: data.user });
        } else {
          setRecipientLookup({ status: 'not_found' });
        }
      } catch { setRecipientLookup({ status: 'not_found' }); }
    }, 600);
    return () => { if (lookupTimeoutRef.current) clearTimeout(lookupTimeoutRef.current); };
  }, [recipientInput]);

  const fetchAvailability = useCallback(async (address: string): Promise<'free' | 'busy' | 'unknown'> => {
    try {
      const gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim();
      if (!gatewayUrl) return 'unknown';
      const res = await fetch(`${gatewayUrl}/api/email/presence?addresses=${encodeURIComponent(address)}`);
      if (!res.ok) return 'unknown';
      const data = await res.json();
      const peerPresence = data.presence?.[address];
      if (peerPresence?.availability) {
        return checkSlotAvailability(peerPresence.availability.slots, peerPresence.availability.windowStart, eventStartMs);
      }
    } catch { /* ignore */ }
    return 'unknown';
  }, [eventStartMs]);

  const addContact = useCallback(async (address: string, displayName: string) => {
    if (recipients.some(r => r.address === address)) return;
    // Prevent adding own key as MobyNet peer (would crash storeMessage from===to).
    // Users CAN still invite their own email as an external recipient for multi-device onboarding.
    if (session && address === session.address) {
      toast.info('Use your email address to invite another device');
      setRecipientInput('');
      setShowContactPicker(false);
      return;
    }
    const availability = await fetchAvailability(address);
    setRecipients(prev => [...prev, { address, displayName, type: 'mobynet', availability }]);
    setRecipientInput('');
    setShowContactPicker(false);
  }, [recipients, fetchAvailability, session]);

  const addExternalRecipient = useCallback(() => {
    const email = recipientInput.trim();
    if (!email || recipients.some(r => r.address === email)) return;
    if (recipientLookup.status === 'found' && recipientLookup.user) {
      // Email resolved to a MobyNet user — but if it's our own key, add as external
      // so the email CTA is sent (multi-device / new-peer onboarding via own email)
      if (session && recipientLookup.user.publicKey === session.address) {
        setRecipients(prev => [...prev, { address: email, displayName: email, type: 'external', availability: 'unknown' }]);
      } else {
        addContact(recipientLookup.user.publicKey, recipientLookup.user.preferredName || email);
      }
    } else {
      setRecipients(prev => [...prev, { address: email, displayName: email, type: 'external', availability: 'unknown' }]);
    }
    setRecipientInput('');
    setRecipientLookup({ status: 'idle' });
  }, [recipientInput, recipientLookup, recipients, addContact, session]);

  const removeRecipient = useCallback((address: string) => {
    setRecipients(prev => prev.filter(r => r.address !== address));
  }, []);

  // ── Save handler ──
  const handleSave = useCallback(async () => {
    if (!title.trim() || !session) return;
    setSaving(true);
    try {
      const d = eventDate;
      const start = allDay
        ? new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
        : new Date(d.getFullYear(), d.getMonth(), d.getDate(), startHour, startMinute).getTime();
      const end = allDay
        ? start + 86_400_000 - 1
        : new Date(d.getFullYear(), d.getMonth(), d.getDate(), endHour, endMinute).getTime();

      // Build updated attendees — preserve existing RSVP states
      const updatedAttendees: CalendarAttendee[] = recipients.map(r => {
        const existing = event.attendees.find(a => a.address === r.address);
        return existing ?? { address: r.address, displayName: r.displayName, rsvp: CalendarRSVP.PENDING, isOrganizer: false };
      });
      const organizer = event.attendees.find(a => a.isOrganizer);
      if (organizer && !updatedAttendees.some(a => a.address === organizer.address)) {
        updatedAttendees.unshift(organizer);
      }

      // Auto-create group chat if toggle is ON
      let groupAddress = event.groupAddress || undefined;
      if (autoCreateGroup && !groupAddress) {
        const mobynetAddresses = recipients.filter(r => r.type === 'mobynet').map(r => r.address);
        groupAddress = await createGroupChatForEvent(db, session, node, title.trim(), mobynetAddresses);
        toast.success(`Group "${title.trim()}" created`);
      }

      await updateEvent(event.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        location: location.trim() || undefined,
        startTime: start,
        endTime: end,
        allDay,
        visibility,
        attendees: updatedAttendees,
        groupAddress,
      });

      // Handle newly added attendees — create invite records + emails
      const newRecipients = recipients.filter(r => !originalAttendees.has(r.address));
      if (newRecipients.length > 0) {
        const groupName = groupAddress
          ? ((organizerGroups ?? []).find(g => g.address === groupAddress)?.name || title.trim())
          : undefined;
        const eventSnapshot = JSON.stringify({
          title: title.trim(), startTime: start, endTime: end, allDay,
          organizerName: session.name || session.address.slice(0, 8),
          groupAddress, groupName,
        });
        for (const r of newRecipients) {
          await db.calendarInvites.add({
            id: crypto.randomUUID(), eventId: event.id,
            senderAddress: session.address, recipientAddress: r.address,
            rsvp: 'pending', sentAt: Date.now(), deliveredP2P: 0, eventSnapshot,
          } as any);
        }

        sendCalendarInviteEmails(
          newRecipients,
          { id: event.id, title: title.trim(), startTime: start, endTime: end, allDay },
          session, db, groupAddress, groupName,
        ).catch(err => console.warn('[EDIT_EVENT] Email notifications failed (non-fatal):', err));

        // Trigger P2P delivery for new attendees
        window.dispatchEvent(new CustomEvent('calendar-event-created', {
          detail: { event: { ...event, attendees: updatedAttendees, groupAddress }, inviteSnapshot: eventSnapshot },
        }));
      }

      onClose();
    } catch (err) {
      console.error('[EDIT_EVENT] Save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [title, description, location, startHour, startMinute, endHour, endMinute, allDay, visibility,
    recipients, autoCreateGroup, session, event, eventDate, updateEvent, onClose, db, node,
    organizerGroups, originalAttendees]);

  const rsvpColor: Record<string, string> = {
    pending: 'text-on-surface-variant', accepted: 'text-green-600',
    declined: 'text-error', tentative: 'text-tertiary',
  };

  return (
    <div className="mt-2 bg-surface-container rounded-xl p-4 space-y-3 border border-outline-variant">
      <div className="flex items-center justify-between">
        <h4 className="text-title-sm text-primary flex items-center gap-2">
          <PencilIcon size={14} /> Edit Event
        </h4>
        <button onClick={onClose} className="p-0.5 rounded-full hover:bg-on-surface/10 text-on-surface-variant" title="Close">
          <XIcon size={14} />
        </button>
      </div>

      {/* Title */}
      <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Event title"
        className="w-full rounded-lg bg-surface-container-highest px-3 py-2 text-body-sm border border-outline-variant focus:border-primary outline-none" />

      {/* All-day toggle + time pickers */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer text-body-sm">
          <input type="checkbox" checked={allDay} onChange={e => setAllDay(e.target.checked)} className="accent-primary size-4" />
          All day
        </label>
        {!allDay && (
          <div className="flex items-center gap-1 text-body-sm">
            <select value={startHour} onChange={e => setStartHour(Number(e.target.value))} title="Start hour"
              className="bg-surface-container-highest rounded px-1.5 py-1 border border-outline-variant text-body-sm">
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>
              ))}
            </select>
            <span>:</span>
            <select value={startMinute} onChange={e => setStartMinute(Number(e.target.value))} title="Start minute"
              className="bg-surface-container-highest rounded px-1.5 py-1 border border-outline-variant text-body-sm">
              {[0, 15, 30, 45].map(m => (<option key={m} value={m}>{String(m).padStart(2, '0')}</option>))}
            </select>
            <span className="mx-1">&ndash;</span>
            <select value={endHour} onChange={e => setEndHour(Number(e.target.value))} title="End hour"
              className="bg-surface-container-highest rounded px-1.5 py-1 border border-outline-variant text-body-sm">
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>
              ))}
            </select>
            <span>:</span>
            <select value={endMinute} onChange={e => setEndMinute(Number(e.target.value))} title="End minute"
              className="bg-surface-container-highest rounded px-1.5 py-1 border border-outline-variant text-body-sm">
              {[0, 15, 30, 45].map(m => (<option key={m} value={m}>{String(m).padStart(2, '0')}</option>))}
            </select>
          </div>
        )}
      </div>

      {/* Location */}
      <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="Location (optional)"
        className="w-full rounded-lg bg-surface-container-highest px-3 py-2 text-body-sm border border-outline-variant focus:border-primary outline-none" />

      {/* Description */}
      <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" rows={2}
        className="w-full rounded-lg bg-surface-container-highest px-3 py-2 text-body-sm border border-outline-variant focus:border-primary outline-none resize-none" />

      {/* Visibility */}
      <div className="flex items-center gap-2">
        <label className="text-label-sm text-on-surface-variant">Show as:</label>
        <select value={visibility} onChange={e => setVisibility(e.target.value as EventVisibility)} title="Event visibility"
          className="bg-surface-container-highest rounded px-2 py-1 border border-outline-variant text-body-sm">
          <option value="busy">Busy</option>
          <option value="free">Free</option>
          <option value="tentative">Tentative</option>
        </select>
      </div>

      {/* ── Attendees ── */}
      <div className="space-y-2">
        <label className="text-label-md text-on-surface flex items-center gap-1">
          <UserPlusIcon size={14} /> Attendees
        </label>

        {recipients.length > 0 && (
          <div className="space-y-1">
            {recipients.map(r => {
              const existing = event.attendees.find(a => a.address === r.address);
              const isNew = !originalAttendees.has(r.address);
              return (
                <div key={r.address} className="flex items-center justify-between text-body-sm">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <AvailabilityDot status={r.availability} />
                    <span className="truncate max-w-[160px]">{r.displayName}</span>
                    {r.type === 'mobynet' && <CheckIcon size={12} className="text-tertiary shrink-0" />}
                    {isNew && <span className="text-label-sm text-primary font-medium">new</span>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {existing && (
                      <span className={`text-label-sm ${rsvpColor[existing.rsvp] ?? 'text-on-surface-variant'}`}>
                        {existing.rsvp.charAt(0).toUpperCase() + existing.rsvp.slice(1)}
                      </span>
                    )}
                    <button onClick={() => removeRecipient(r.address)}
                      className="p-0.5 rounded-full hover:bg-on-surface/10 text-on-surface-variant" title="Remove">
                      <XIcon size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Input for adding recipients */}
        <div className="relative">
          <input type="text" value={recipientInput}
            onChange={e => { setRecipientInput(e.target.value); setShowContactPicker(true); }}
            onFocus={() => setShowContactPicker(true)}
            onKeyDown={e => { if (e.key === 'Enter' && recipientInput.includes('@')) { e.preventDefault(); addExternalRecipient(); } }}
            placeholder="Add contact or email..."
            className="w-full rounded-lg bg-surface-container-highest px-3 py-2 text-body-sm border border-outline-variant focus:border-primary outline-none" />

          {showContactPicker && filteredContacts && filteredContacts.length > 0 && !recipientInput.includes('@') && (
            <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-surface-container-high border border-outline-variant rounded-lg shadow-lg max-h-32 overflow-y-auto">
              {filteredContacts.slice(0, 6).map(c => (
                <button key={c.address}
                  onClick={() => addContact(c.address, c.card?.name || c.nickname || c.address.slice(0, 8))}
                  className="w-full text-left px-3 py-1.5 text-body-sm hover:bg-on-surface/5 flex items-center gap-2 transition">
                  <CheckIcon size={12} className="text-tertiary" />
                  <span className="truncate">{c.card?.name || c.nickname || c.address.slice(0, 8)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {recipientInput.includes('@') && recipientLookup.status !== 'checking' && (
          <button onClick={addExternalRecipient} className="text-label-sm text-primary hover:underline">
            + Add &quot;{recipientInput}&quot; as {recipientLookup.status === 'found' ? 'MobyNet contact' : 'external recipient'}
          </button>
        )}
      </div>

      {/* Auto-create group toggle */}
      {recipients.length > 1 && !event.groupAddress && (
        <label className="flex items-center gap-2 cursor-pointer text-body-sm bg-tertiary/10 rounded-lg p-2.5">
          <input type="checkbox" checked={autoCreateGroup} onChange={e => setAutoCreateGroup(e.target.checked)}
            className="accent-tertiary size-4" />
          <UsersIcon size={14} className="text-tertiary" />
          <span>Create group chat &ldquo;{title.trim() || 'Untitled'}&rdquo;</span>
        </label>
      )}

      {/* Existing group indicator */}
      {event.groupAddress && (
        <div className="flex items-center gap-2 text-body-sm text-on-surface-variant bg-surface-container-highest rounded-lg p-2.5">
          <UsersIcon size={14} className="text-tertiary" />
          Linked to group: {(organizerGroups ?? []).find(g => g.address === event.groupAddress)?.name || 'Group'}
          <CrownIcon size={12} className="text-tertiary" />
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button onClick={handleSave} disabled={!title.trim() || saving}
          className="flex-1 rounded-full bg-primary text-on-primary py-2 text-label-md hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          {saving && <Loader2 size={14} className="animate-spin" />}
          Save Changes
        </button>
        <button onClick={onClose}
          className="rounded-full border border-outline-variant px-4 py-2 text-label-md text-on-surface hover:bg-on-surface/5 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Main Dialog ────────────────────────────────────────────────────────────

export function CalendarDayDialog() {
  const [dateMs, setDateMs] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const { deleteEvent } = useCalendarCRUD();

  // Listen for day-open events from CalendarPanel
  useEffect(() => {
    const handler = (e: CustomEvent<{ date: number }>) => {
      setDateMs(e.detail.date);
      setShowCreateForm(false);
    };
    window.addEventListener('calendar-day-open', handler as EventListener);
    return () => window.removeEventListener('calendar-day-open', handler as EventListener);
  }, []);

  const close = useCallback(() => {
    setDateMs(null);
    setShowCreateForm(false);
    setEditingEventId(null);
  }, []);

  if (dateMs === null) return null;

  return (
    <CalendarDayDialogContent
      dateMs={dateMs}
      showCreateForm={showCreateForm}
      setShowCreateForm={setShowCreateForm}
      editingEventId={editingEventId}
      setEditingEventId={setEditingEventId}
      onDelete={deleteEvent}
      onClose={close}
    />
  );
}

/** Inner content — separated so hooks are only active when dialog is open */
function CalendarDayDialogContent({
  dateMs,
  showCreateForm,
  setShowCreateForm,
  editingEventId,
  setEditingEventId,
  onDelete,
  onClose,
}: {
  dateMs: number;
  showCreateForm: boolean;
  setShowCreateForm: (v: boolean) => void;
  editingEventId: string | null;
  setEditingEventId: (id: string | null) => void;
  onDelete: (id: string) => Promise<void>;
  onClose: () => void;
}) {
  const [dayStart, dayEnd] = useMemo(() => dayBounds(dateMs), [dateMs]);
  const events = useDayEvents(dayStart, dayEnd);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await onDelete(id);
      if (editingEventId === id) setEditingEventId(null);
    } catch (err) {
      console.error('[CALENDAR_DAY_DIALOG] Delete failed:', err);
    }
  }, [onDelete, editingEventId, setEditingEventId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-surface-container-high text-on-surface rounded-extra-large shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-outline-variant">
          <h2 className="text-title-lg">{formatDayHeader(dateMs)}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-on-surface/8 transition"
            aria-label="Close"
          >
            <XIcon size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Agenda */}
          {events.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-label-md text-on-surface-variant uppercase tracking-wide">
                {events.length} event{events.length !== 1 ? 's' : ''}
              </h3>
              {events.map(ev => (
                <div key={ev.id}>
                  <AgendaItem
                    event={ev}
                    onDelete={handleDelete}
                    onEdit={id => setEditingEventId(editingEventId === id ? null : id)}
                  />
                  {editingEventId === ev.id && (
                    <EditEventForm
                      event={ev}
                      onClose={() => setEditingEventId(null)}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {events.length === 0 && !showCreateForm && (
            <div className="text-center py-6 text-on-surface-variant">
              <ClockIcon size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-body-md">No events this day</p>
            </div>
          )}

          {/* Divider before form */}
          {showCreateForm && events.length > 0 && (
            <div className="border-t border-outline-variant" />
          )}

          {/* Create form or button */}
          {showCreateForm ? (
            <NewEventForm
              dateMs={dateMs}
              onCreated={() => setShowCreateForm(false)}
              onCancel={() => setShowCreateForm(false)}
            />
          ) : (
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full flex items-center justify-center gap-2 rounded-full border-2 border-dashed border-outline-variant py-3 text-label-lg text-on-surface-variant hover:border-primary hover:text-primary transition-colors"
            >
              <PlusIcon size={18} />
              New Event
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
